(function () {
  // lastCart is the single source of predicted truth. It is updated
  // SYNCHRONOUSLY the instant an action fires (not when the network resolves),
  // so a second action started before the first round-trips still branches
  // from the already-mutated state instead of a stale snapshot.
  var lastCart = window.__initialCart || null;
  // Monotonic token bumped on every client-initiated mutation. A network
  // response may only write back to lastCart / re-render if its captured token
  // is still the latest — otherwise a slow earlier request would clobber a
  // newer prediction (the "removed item comes back" bug).
  var cartSeq = 0;
  // MUST equal --cart-remove-fade in style-globals.css.liquid (0.702s) — the
  // fade-out runs for this long, then the swap + fade-in fire. If these diverge,
  // the checkout label / subtotal / images won't fade in lockstep.
  var REMOVE_FADE_MS = 702;

  // Already-decoded PDP images, keyed by variant id (as a string). The PDP
  // renders its main images at `master` size; the cart renders `_1200x` — a
  // DIFFERENT URL, so the cart image is never a cache hit and pops in late,
  // out of sync with the drawer animation. On add-to-cart we capture the
  // currently-visible PDP image (which the browser has ALREADY decoded) and
  // reuse it as the cart thumbnail for that variant, so it paints instantly
  // in step with the drawer. Purely presentational — never touches lastCart.
  var primedImages = {};

  // The product image the shopper is currently looking at, scoped to the
  // product layout that owns this form (so the PDP modal and the page behind
  // it can't cross-match). offsetParent === null skips display:none images
  // (e.g. the hidden #variant-images container), leaving the visible main one.
  function visiblePdpImageSrc(form) {
    var scope =
      (form && form.closest && form.closest(".product-page-wrapper")) || document;
    var imgs = scope.querySelectorAll("img.product-page-image");
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.offsetParent === null) continue;
      var src = img.currentSrc || img.src;
      if (src) return src;
    }
    return null;
  }

  // Manually fade an element's opacity from→to over REMOVE_FADE_MS using rAF.
  // Used for the CHECKOUT label on last-item removal: a CSS transition and the
  // Web Animations API both refused to render the 1→0 step on that element, so
  // we set inline opacity every frame with transition:none — nothing in the
  // cascade (transitions/animations) can then override or swallow it. The active
  // rAF id is tracked per-element so a fade-in cleanly supersedes the fade-out.
  var _labelFadeRAF = null;
  function rampLabelOpacity(el, from, to) {
    if (!el) return;
    if (_labelFadeRAF) cancelAnimationFrame(_labelFadeRAF);
    el.style.transition = "none";
    var start = null;
    function step(ts) {
      if (start === null) start = ts;
      var t = Math.min(1, (ts - start) / REMOVE_FADE_MS);
      // ease-in-out, matches the CSS curve used elsewhere
      var eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      el.style.opacity = String(from + (to - from) * eased);
      if (t < 1) {
        _labelFadeRAF = requestAnimationFrame(step);
      } else {
        _labelFadeRAF = null;
      }
    }
    _labelFadeRAF = requestAnimationFrame(step);
  }

  function updateCartCountDisplay(count) {
    var el = document.getElementById("cart-count");
    if (!el) return;
    if (count > 0) {
      el.textContent = count;
      el.style.opacity = "1";
      el.style.visibility = "visible";
    } else {
      el.textContent = "";
      el.style.opacity = "0";
      el.style.visibility = "hidden";
    }
  }

  // Format price based on market/currency. Copied verbatim from the old
  // backup/menu-cart/script-cart.js so the locale-detection contract stays
  // identical — Shopify.currency.active is the source of truth, JPY uses
  // the ja-JP numeric grouping, anything else falls back to USD.
  function formatLocalizedPrice(cents) {
    var market =
      (window.Shopify && Shopify.currency && Shopify.currency.active) || "USD";
    if (market === "JPY" || market === "JP") {
      return "¥" + Math.round(cents / 100).toLocaleString("ja-JP");
    }
    return "$" + (cents / 100).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  function updateCartTotals(cart) {
    var formatted = formatLocalizedPrice(cart.total_price);
    var subtotalEl = document.getElementById("cart-subtotal-amount");
    if (subtotalEl) subtotalEl.textContent = formatted;

    var checkoutEl = document.getElementById("cart-checkout");
    if (checkoutEl) {
      // Write the TEXT into the inner label span (created in navigation.liquid)
      // so the button frame is untouched and only the label crossfades. Fall
      // back to the anchor itself if the span is somehow absent.
      var labelEl = checkoutEl.querySelector(".cart-checkout-label") || checkoutEl;
      if (!cart.item_count) {
        // Empty cart: disable the button and relabel it.
        labelEl.textContent = "YOUR CART IS EMPTY";
        checkoutEl.classList.add("is-empty");
        checkoutEl.removeAttribute("href");
        checkoutEl.removeAttribute("data-formatted-price");
      } else {
        labelEl.textContent = "CHECKOUT";
        checkoutEl.classList.remove("is-empty");
        checkoutEl.setAttribute("data-formatted-price", formatted);
        checkoutEl.setAttribute(
          "href",
          formatted.indexOf("¥") !== -1 ? "/checkout?locale=ja" : "/checkout"
        );
      }
    }
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  // Rebuilds the line items list from a /cart.js response. Mirrors the
  // Liquid render in snippets/navigation.liquid: nested loop (item × quantity)
  // so each unit is its own line, numbered globally across all items.
  // Match the Liquid render's `img_url: '1200x'`: insert Shopify's _1200x size
  // token before the extension. Smaller than the raw original (so it paints
  // fast on add) and identical to the server-rendered URL (so it's cached).
  function cartImageSrc(url) {
    if (!url) return url;
    return url.replace(/(\.(?:jpe?g|png|gif|webp))(\?|$)/i, "_1200x$1$2");
  }

  // Warm the browser cache with the cart's product images even while the cart is
  // CLOSED. On mobile the drawer is display:none when shut, so its server-
  // rendered <img>s never fetch until the cart opens (and freshly-built <img>s
  // from renderCartImages also need to load). Prefetching the canonical _1200x
  // URLs here means the cart paints full instantly on open, regardless of state.
  var prefetchedCartImages = Object.create(null);
  function prefetchCartImages(cart) {
    if (!cart || !cart.items) return;
    cart.items.forEach(function (item) {
      var src = cartImageSrc(item.image);
      if (!src || prefetchedCartImages[src]) return;
      prefetchedCartImages[src] = true;
      var img = new Image();
      img.decoding = "async";
      img.src = src; // request lands in the HTTP cache; the real cart img reuses it
    });
  }

  function renderCartImages(cart) {
    var container = document.getElementById("cart-images");
    if (!container) return;
    // Idempotent: skip the rebuild when the item set is unchanged, so the
    // optimistic render and the authoritative reconcile don't double-render
    // (flash). Safe now that lastCart is authoritative (add uses /cart.js).
    var sig = (cart.items || []).map(function (it) {
      return it.key + ":" + it.quantity;
    }).join("|");
    if (container.getAttribute("data-sig") === sig) return;
    var html = "";
    var count = 0;
    cart.items.forEach(function (item) {
      // Prefer the already-decoded PDP image for this variant when we have it
      // (added this session) so the thumbnail paints instantly with the drawer
      // animation; fall back to the canonical _1200x URL otherwise.
      var primed = item.variant_id != null ? primedImages[String(item.variant_id)] : null;
      var src = primed || cartImageSrc(item.image);
      for (var i = 0; i < item.quantity; i++) {
        count++;
        html +=
          '<div class="cart-product">' +
            '<img class="cart-image" src="' + escapeHtml(src) +
              '" alt="' + escapeHtml(item.title || "") + '">' +
            '<div class="cart-product-overlay">' +
              '<div class="cart-product-info">' +
                '<div class="cart-product-meta">' +
                  '<span class="cart-product-sku">' + escapeHtml(item.sku) + '</span>' +
                  '<span class="cart-product-price">' + formatLocalizedPrice(item.price) + '</span>' +
                '</div>' +
                '<button class="cart-product-remove" type="button" data-item-key="' +
                  escapeHtml(item.key) + '" data-quantity="' + item.quantity + '">REMOVE</button>' +
              '</div>' +
            '</div>' +
          '</div>';
      }
    });
    // Pure rebuild. Visibility is entirely the cart's job (the drawer's
    // open/close fade on #cart-drawer-inner) — this controller never touches
    // opacity/visibility, so it can't fight the cart animation.
    container.innerHTML = html;
    container.setAttribute("data-count", count);
    container.setAttribute("data-sig", sig);

    // Fade each thumbnail in as it decodes. Images that are already cached/
    // decoded show instantly (no .img-loading); the rest start transparent and
    // ease in when ready — so the drawer can open immediately (no wait) and
    // nothing pops in or flashes.
    container.querySelectorAll(".cart-product").forEach(function (prod) {
      var img = prod.querySelector("img.cart-image");
      if (!img || (img.complete && img.naturalWidth > 0)) return;
      prod.classList.add("img-loading");
      var reveal = function () { prod.classList.remove("img-loading"); };
      if (img.decode) {
        img.decode().then(reveal).catch(reveal);
      } else {
        img.addEventListener("load", reveal, { once: true });
        img.addEventListener("error", reveal, { once: true });
      }
    });

    watchCartImagesForOverflow();
  }

  // Measure now and re-measure as each image decodes — image heights (and thus
  // whether the grid overflows) aren't known until then.
  function watchCartImagesForOverflow() {
    var c = document.getElementById("cart-images");
    if (!c) return;
    updateCartImagesOverflow();
    c.querySelectorAll("img.cart-image").forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) return;
      // Coalesce, don't measure per-image: on a first (uncached) add every
      // thumbnail decodes DURING the open fade, and updateCartImagesOverflow
      // forces a synchronous reflow (alignContent toggle + offsetTop reads) on
      // each call — many of those back-to-back is what makes the first add
      // choppy. Batching to one rAF-measured pass per frame keeps the fade smooth.
      img.addEventListener("load", scheduleOverflowUpdate, { once: true });
    });
  }

  // Collapse a burst of overflow re-measures (e.g. many images decoding during
  // the open animation) into a single rAF-batched pass to avoid layout thrash.
  var overflowRaf = null;
  function scheduleOverflowUpdate() {
    if (overflowRaf) return;
    overflowRaf = requestAnimationFrame(function () {
      overflowRaf = null;
      updateCartImagesOverflow();
    });
  }

  // .is-overflow switches the grid to a top-aligned, inset (50px top close
  // chrome + 150px bottom subtotal/checkout zone), scrollable layout. Because
  // the summary + checkout now OVERLAY the full-height grid, content can sit
  // UNDER them while still "fitting" the box — so we can't just compare against
  // clientHeight (it includes the area behind the overlays). Instead we measure
  // the real content extent and trigger as soon as it reaches into either
  // reserved zone (e.g. ~6 items on a shrunk window), which adds the padding.
  var OVERFLOW_TOP_RESERVE = 50;    // matches .is-overflow padding-top
  var OVERFLOW_BOTTOM_RESERVE = 150; // matches .is-overflow padding-bottom
  function updateCartImagesOverflow() {
    var c = document.getElementById("cart-images");
    if (!c) return;
    c.classList.remove("is-overflow");
    var prevAlign = c.style.alignContent;
    c.style.alignContent = "flex-start"; // top-align so offsets measure from 0
    // scrollHeight clamps to clientHeight when the content fits, so it can't
    // reveal whether the content reaches into the reserved zones. Measure the
    // lowest product's bottom edge (its real layout extent) instead.
    var contentBottom = 0;
    c.querySelectorAll(".cart-product").forEach(function (p) {
      var b = p.offsetTop + p.offsetHeight;
      if (b > contentBottom) contentBottom = b;
    });
    c.style.alignContent = prevAlign;
    var usable = c.clientHeight - OVERFLOW_TOP_RESERVE - OVERFLOW_BOTTOM_RESERVE;
    if (contentBottom > usable + 1) c.classList.add("is-overflow");
  }

  // Disable the product-item hover dim until the pointer next moves. Used after
  // pressing a cart button (open/remove), where the cursor is parked over an
  // item and would otherwise instantly dim it.
  function suppressHoverUntilMove() {
    var el = document.getElementById("cart-images");
    if (!el) return;
    el.classList.add("suppress-hover");
    var clear = function () {
      el.classList.remove("suppress-hover");
      document.removeEventListener("mousemove", clear);
    };
    document.addEventListener("mousemove", clear);
  }

  // Predict the cart after removing one unit of `key` (lastCart is authoritative
  // since add uses /cart.js), so the remove can update instantly without
  // waiting on the network. The real /cart/change reconciles in the background.
  function optimisticRemove(cart, key) {
    if (!cart || !cart.items) return null;
    var items = [];
    var removedUnitPrice = 0;
    cart.items.forEach(function (item) {
      if (item.key === key) {
        removedUnitPrice = (item.final_price != null ? item.final_price : item.price) || 0;
        if (item.quantity > 1) {
          var clone = {};
          for (var k in item) {
            if (Object.prototype.hasOwnProperty.call(item, k)) clone[k] = item[k];
          }
          clone.quantity = item.quantity - 1;
          items.push(clone);
        }
        // quantity 1 → drop the line entirely
      } else {
        items.push(item);
      }
    });
    var item_count = 0;
    items.forEach(function (it) { item_count += it.quantity; });
    return {
      items: items,
      item_count: item_count,
      total_price: Math.max(0, (cart.total_price || 0) - removedUnitPrice),
    };
  }

  function bindRemoveLineItem() {
    document.addEventListener("click", function (e) {
      // The whole text area (SKU + price + REMOVE) removes the item, not just
      // the small REMOVE button — a much easier target, especially on touch.
      var info = e.target.closest && e.target.closest(".cart-product-info");
      if (!info) return;
      var btn = info.querySelector(".cart-product-remove");
      if (!btn || btn.disabled) return;
      e.preventDefault();
      e.stopPropagation();

      btn.disabled = true;

      var key = btn.getAttribute("data-item-key");
      var serverItem = lastCart && lastCart.items
        ? lastCart.items.filter(function (i) { return i.key === key; })[0]
        : null;
      var currentQty = serverItem
        ? serverItem.quantity
        : (parseInt(btn.getAttribute("data-quantity"), 10) || 1);
      var newQty = Math.max(0, currentQty - 1);

      var optimistic = optimisticRemove(lastCart, key);
      // Commit the prediction to lastCart IMMEDIATELY (before the fade, before
      // the network). A subsequent remove — e.g. the 2nd-to-last clicked while
      // this one is still in flight — then branches from the already-reduced
      // cart instead of resurrecting this item.
      if (optimistic) lastCart = optimistic;
      // Broadcast the prediction NOW rather than when /cart/change.js answers.
      // The PDP derives its IN CART lock from the cart, so waiting on the round
      // trip left the add-to-cart button stuck at IN CART for the very unit the
      // shopper had just removed — and when a newer mutation superseded this
      // response (the seq guard below drops it), that release never arrived at
      // all. The reconciling dispatch below still corrects anything we mispredicted.
      if (optimistic) {
        document.dispatchEvent(new CustomEvent("cart:updated", { detail: optimistic }));
      }
      cartSeq++;
      var mySeq = cartSeq;

      var content = document.getElementById("cart-drawer-right-content");
      var checkoutEl = document.getElementById("cart-checkout");
      var checkoutLabel = checkoutEl && checkoutEl.querySelector(".cart-checkout-label");
      var isLastItem = optimistic && optimistic.item_count === 0;

      // Single animation cycle: fade out → rebuild + update totals at near-zero
      // opacity → fade back in. Everything updates together when faded out.
      if (content) content.classList.add("cart-fading");
      // ONLY on the last-item removal, fade the CHECKOUT label in lockstep. Both
      // a CSS transition AND the Web Animations API failed to render the 1→0
      // step on this element (the fade-IN worked, the fade-OUT snapped) — the
      // style engine kept swallowing it. So drive opacity manually, frame by
      // frame, with an inline transition:none so nothing in the cascade can
      // override or coalesce it. updateCartTotals swaps the text to YOUR CART IS
      // EMPTY while it sits at opacity 0, hiding the swap.
      if (isLastItem && checkoutLabel) rampLabelOpacity(checkoutLabel, 1, 0);

      setTimeout(function () {
        // Render the CURRENT predicted state (lastCart), not the captured
        // `optimistic` snapshot: if another remove fired during this fade,
        // lastCart already reflects it, so we paint the combined result and
        // never flash a stale row back in. renderCartImages is idempotent
        // (data-sig), so overlapping fades collapse to one final paint.
        if (lastCart) {
          updateCartCountDisplay(lastCart.item_count);
          updateCartTotals(lastCart);
          renderCartImages(lastCart);
        }
        suppressHoverUntilMove();
        // Fade the grid back in immediately. The remaining items are already
        // decoded (they were just on screen), and renderCartImages fades any
        // not-yet-ready thumbnail in on its own — so nothing blanks out.
        if (content) content.classList.remove("cart-fading");
        // Text was just swapped to YOUR CART IS EMPTY while invisible — fade it
        // back in on the same manual ramp / timing.
        if (isLastItem && checkoutLabel) rampLabelOpacity(checkoutLabel, 0, 1);
      }, REMOVE_FADE_MS);

      // Backend fires in parallel. ONLY updates lastCart — zero DOM writes —
      // so the animation above is never interrupted by a second render. The
      // seq guard drops this response if a newer mutation has since fired,
      // so a slow change.js can't overwrite a fresher prediction.
      fetch("/cart/change.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity: newQty }),
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (mySeq !== cartSeq) return; // superseded by a newer mutation
          lastCart = cart;
          document.dispatchEvent(new CustomEvent("cart:updated", { detail: cart }));
        })
        .catch(function () {
          // Optimistic lastCart already stands; nothing to roll back.
        });
    });
  }

  function refreshCartCount(opts) {
    opts = opts || {};
    // Pin this refresh to a mutation token. Callers that represent a discrete
    // mutation (add-to-cart) pass their own seq so a later remove can supersede
    // them; bare callers (initial load, window.Cart.refresh) bump a fresh token
    // and act as the latest authoritative pull.
    var mySeq = opts.seq != null ? opts.seq : ++cartSeq;
    // no-store: /cart.js can be browser-cached, which returns a STALE cart
    // after an add/remove — making the display look like "nothing was added"
    // and feeding the remove a stale lastCart. Always fetch fresh.
    return fetch("/cart.js", { credentials: "same-origin", cache: "no-store" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (mySeq !== cartSeq) return cart; // a newer mutation already won
        lastCart = cart;
        updateCartCountDisplay(cart.item_count);
        updateCartTotals(cart);
        // On initial page load the server-rendered (Liquid) cart images are
        // already correct. Rebuilding them here would replace that DOM with
        // fresh <img>s that must reload — which flashes ("show then cut out")
        // if the drawer is opened around the time this fetch resolves. Only
        // rebuild when a client-side mutation actually changed the cart.
        if (!opts.skipImages) {
          renderCartImages(cart);
        }
        document.dispatchEvent(new CustomEvent("cart:updated", { detail: cart }));
        return cart;
      })
      .catch(function () {});
  }

  // Open/close state is owned by HeaderState (script-navigation.js). This
  // file binds the click handlers that fire those methods and reads back
  // state via HeaderState.state.cartOpen.
  function isCartOpen() {
    return window.HeaderState && window.HeaderState.state.cartOpen;
  }

  function isCartEmpty() {
    return !lastCart || !lastCart.item_count;
  }

  function bindCartToggle() {
    var icon = document.getElementById("cart-count");
    if (icon) {
      icon.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        // Toggle: when the PDP modal is open, #index-menu is visibility:hidden
        // so the normal fall-through close path (click → menu capture handler)
        // is broken. The cart-count click handler must close directly here.
        if (window.HeaderState && window.HeaderState.state.cartOpen) {
          window.HeaderState.closeCart();
        } else {
          window.HeaderState.openCart();
        }
      });
    }

    // Filter bar click while cart is open closes the cart. Capture phase so
    // we run before script-index-filters.js's bubble-phase handler and can
    // stop it from interpreting the click as a filter-bar dismissal.
    var menu = document.getElementById("index-menu");
    if (menu) {
      menu.addEventListener("click", function (e) {
        if (!isCartOpen()) return;
        e.stopPropagation();
        window.HeaderState.closeCart();
      }, true);
    }

    // Outside click closes (anything not inside the drawer's inner content).
    // NOTE: logo clicks never reach here — the nav owns them (script-navigation
    // .js's logo handler goes home + stopPropagation when the cart is open; on
    // non-index templates the logo is a native <a href="/">).
    document.addEventListener("click", function (e) {
      if (!isCartOpen()) return;
      // Normally clicks inside the drawer content are ignored (so you can use
      // the cart). But an EMPTY cart has nothing to interact with, so clicking
      // anywhere in the container closes the overlay too.
      if (e.target.closest("#cart-drawer-inner") && !isCartEmpty()) return;
      if (e.target.closest("#cart-count")) return;
      if (e.target.closest("#index-menu")) return; // handled by capture listener above
      window.HeaderState.closeCart();
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && isCartOpen()) window.HeaderState.closeCart();
    });
  }

  // A button whose SOLD OUT / IN CART label is showing is locked by the PDP's
  // inventory logic (script-product.js → applyButtonState) and must NOT be
  // re-enabled by this file. IN CART is the state that matters most here: it is
  // created BY an add (you now hold every tracked unit), so the add flow that
  // finishes right afterwards is exactly what used to unlock it again and let
  // the shopper buy stock that no longer exists.
  function isAddButtonLocked(form) {
    if (!form) return false;
    var locks = form.querySelectorAll(".sold-out-text, .in-cart-text");
    for (var i = 0; i < locks.length; i++) {
      if (getComputedStyle(locks[i]).display !== "none") return true;
    }
    return false;
  }

  function bindAddToCartForms() {
    document.addEventListener("submit", function (e) {
      var form = e.target;
      if (!form || form.tagName !== "FORM") return;
      var action = form.getAttribute("action") || "";
      if (!/\/cart\/add$/.test(action) && form.id !== "add-to-cart-form") return;

      e.preventDefault();

      var button = form.querySelector('button[type="submit"]');
      var loader = form.querySelector(".button-loading");
      var btnContent = form.querySelector(".product-page-button-content");

      // In-flight lock lives on the FORM (the `submitting` class), NOT on
      // button.disabled. button.disabled is independently owned by the variant
      // inventory logic (script-product-variants.js), which re-executes every
      // time the PDP modal opens — sharing that flag let the inventory code
      // release/re-assert the add lock out from under an in-flight request,
      // which is what made repeat-adds inconsistent. The form class is local
      // to this submit and untouched by anyone else.
      if (form.classList.contains("submitting")) return;
      // Belt and braces: a locked button should already be disabled, but if it
      // was stranded enabled, refuse the add rather than oversell.
      if (isAddButtonLocked(form)) {
        if (button) button.disabled = true;
        return;
      }

      if (button) button.disabled = true;
      if (loader) loader.style.display = "flex";
      if (btnContent) btnContent.style.display = "none";
      form.classList.add("submitting");

      // Claim a mutation token for this add. If the user removes something
      // before /cart.js resolves, that remove bumps cartSeq and this add's
      // refresh is dropped rather than clobbering the newer prediction.
      cartSeq++;
      var addSeq = cartSeq;

      // Capture the image the shopper is looking at RIGHT NOW (synchronously,
      // before any await) so renderCartImages can reuse this already-decoded
      // bitmap for the added variant instead of fetching a fresh _1200x.
      var addedVariantId = form.querySelector('input[name="id"]');
      addedVariantId = addedVariantId ? addedVariantId.value : null;
      if (addedVariantId) {
        var pdpSrc = visiblePdpImageSrc(form);
        if (pdpSrc) primedImages[String(addedVariantId)] = pdpSrc;
      }

      // `rejected` = the server refused this add (422 and friends), as opposed
      // to it succeeding or the network simply failing. Only a refusal tells us
      // anything about stock; a dropped connection tells us nothing.
      function finish(rejected) {
        // Hand the button back to its owner. refreshCartCount has already
        // dispatched cart:updated by the time this runs on the success path, so
        // the PDP has re-derived the state (an add that consumed the last unit
        // is now IN CART) — clearing `disabled` unconditionally here is what
        // let a shopper add the same last unit twice. Ask the PDP to re-assert
        // when it's present; otherwise fall back to the label check.
        if (loader) loader.style.display = "none";
        if (btnContent) btnContent.style.display = "flex";
        form.classList.remove("submitting");
        // Settle this add against the PDP's optimistic hold BEFORE reading the
        // lock back off the labels: the hold is what kept the button disabled
        // while the request was in flight, and only the request knows it is over.
        if (addedVariantId) {
          if (rejected && window.__pdpAddRejected) {
            window.__pdpAddRejected(addedVariantId);
          } else if (window.__pdpAddSettled) {
            window.__pdpAddSettled(addedVariantId);
          }
        }
        if (button) button.disabled = isAddButtonLocked(form);
        if (window.__pdpSyncAddButton) window.__pdpSyncAddButton();
      }

      fetch("/cart/add.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        body: new FormData(form),
      })
        .then(function (r) {
          if (!r.ok) {
            // A 4xx from /cart/add.js is Shopify telling us this page's
            // inventory snapshot is stale ("All 1 X are in your cart"). Carry
            // that verdict to the catch so finish() can lock the button instead
            // of leaving it on ADD TO CART for a unit that cannot be bought.
            return r
              .json()
              .catch(function () { return null; })
              .then(function (body) {
                var err = new Error("cart/add.js HTTP " + r.status);
                err.rejected = r.status >= 400 && r.status < 500;
                err.description = body && (body.description || body.message);
                throw err;
              });
          }
          return r.json();
        })
        .then(function () {
          return refreshCartCount({ seq: addSeq });
        })
        .then(function () {
          // Re-enable the button and open the cart immediately — no waiting on
          // image decode. renderCartImages fades each thumbnail in as it's
          // ready (cached ones instantly), so the drawer is responsive even for
          // a non-cached product and nothing pops in.
          finish();
          if (window.HeaderState) window.HeaderState.openCart();
        })
        .catch(function (err) {
          console.warn("[cart] add-to-cart failed:", err);
          // Re-enable the button immediately on failure too, then refresh and
          // open the cart so the user still sees current state. A REFUSED add
          // is different: hand the PDP the correction so the button lands on
          // SOLD OUT / IN CART rather than inviting the same failing click.
          finish(!!(err && err.rejected));
          refreshCartCount({ seq: addSeq })
            .then(function () {
              if (window.HeaderState) window.HeaderState.openCart();
            })
            .catch(function () {});
        });
    }, true);
  }

  // Touch devices have no :hover, so the product overlay (SKU / price / REMOVE)
  // is unreachable. On no-hover devices, tap a product to reveal it; it
  // auto-hides after 5s of no interaction, and tapping another product moves
  // the reveal there (one at a time). Desktop keeps using :hover.
  function bindCartProductTapReveal() {
    if (window.matchMedia && window.matchMedia("(hover: hover)").matches) return;
    var REVEAL_MS = 5000;
    var timer = null;
    function clearReveal() {
      var cur = document.querySelector("#cart-images .cart-product.revealed");
      if (cur) cur.classList.remove("revealed");
      if (timer) { clearTimeout(timer); timer = null; }
    }
    document.addEventListener("click", function (e) {
      // Tapping the text area removes the item — handled by bindRemoveLineItem.
      if (e.target.closest(".cart-product-info")) return;
      // Reveal is tied to the MEDIA itself. Tapping any negative space (no
      // media item — the gaps, the revealed overlay's empty area, etc.)
      // un-reveals the last revealed product.
      var img = e.target.closest && e.target.closest("#cart-images .cart-image");
      if (!img) {
        clearReveal();
        return;
      }
      var prod = img.closest(".cart-product");
      if (!prod) return;
      if (!prod.classList.contains("revealed")) {
        clearReveal();              // shift away from any other revealed product
        prod.classList.add("revealed");
      }
      if (timer) clearTimeout(timer); // (re)start the 5s auto-hide on each tap
      timer = setTimeout(function () {
        prod.classList.remove("revealed");
        timer = null;
      }, REVEAL_MS);
    });
    // Don't leave a product revealed across cart open/close cycles.
    document.addEventListener("cart:closed", clearReveal);
  }

  document.addEventListener("DOMContentLoaded", function () {
    // skipImages: trust the server-rendered cart images on load; only sync
    // count/totals. Client-side add/remove still rebuild the images.
    refreshCartCount({ skipImages: true });
    // Always warm the cart images in the background, even while the cart stays
    // closed — from the server-rendered initial cart now, and again whenever the
    // cart changes — so opening the cart never shows empty/loading thumbnails.
    prefetchCartImages(lastCart);
    document.addEventListener("cart:updated", function (e) {
      prefetchCartImages(e.detail);
    });

    // bfcache restore (iOS Safari's Back is almost always this): the page comes
    // back with its DOM and JS state frozen exactly as it left — no reload, no
    // DOMContentLoaded, no cart:updated. Everything derived from the cart is
    // therefore stale, including the PDP's add-to-cart lock, which would be
    // showing ADD TO CART for stock that is now entirely in the cart. Re-pull
    // and re-broadcast. skipImages: the restored thumbnails are still the right
    // ones, and rebuilding them would flash the drawer for no reason.
    window.addEventListener("pageshow", function (e) {
      if (e.persisted) refreshCartCount({ skipImages: true });
    });

    bindCartToggle();
    bindAddToCartForms();
    bindRemoveLineItem();
    bindCartProductTapReveal();
    // After the cart opens (e.g. via add-to-cart), the cursor is often parked
    // over a product image — suppress the hover dim until it moves.
    document.addEventListener("cart:opened", suppressHoverUntilMove);
    // The initial cart is server-rendered (renderCartImages is skipped on load),
    // so measure overflow when it first opens and whenever the viewport resizes.
    document.addEventListener("cart:opened", watchCartImagesForOverflow);
    window.addEventListener("resize", scheduleOverflowUpdate);

    // Safety net: whenever the cart closes, guarantee every add-to-cart button
    // is back to its ready state. finish() already does this in the normal add
    // flow, but a stranded in-flight lock (a request that never settled, or a
    // button left disabled by a modal re-exec) could otherwise leave the button
    // stuck. We only touch buttons that are NOT legitimately locked: a variant
    // with no purchasable stock left shows .sold-out-text or .in-cart-text, and
    // a sold-out product uses a separate type="button" form, so none of those
    // are re-enabled here.
    document.addEventListener("cart:closed", function () {
      document.querySelectorAll("#add-to-cart-form").forEach(function (form) {
        var button = form.querySelector('button[type="submit"]');
        if (!button) return; // sold-out product form has a type="button" instead
        if (isAddButtonLocked(form)) return;
        form.classList.remove("submitting");
        button.disabled = false;
        var loader = form.querySelector(".button-loading");
        var btnContent = form.querySelector(".product-page-button-content");
        if (loader) loader.style.display = "none";
        if (btnContent) btnContent.style.display = "flex";
      });
    });

    // Subtotal + checkout are transparent overlays floating over the bottom of
    // the scrolling image grid. Wheel events over them must still scroll the
    // grid: the checkout is clickable (blocks native pass-through) and native
    // pass-through through the pointer-events:none subtotal proved unreliable.
    // So forward the scroll directly whenever the cursor is within either
    // overlay's box, and preventDefault so we never double-scroll.
    function rectContains(el, x, y) {
      if (!el) return false;
      var r = el.getBoundingClientRect();
      return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
    }
    document.addEventListener(
      "wheel",
      function (e) {
        if (!document.body.classList.contains("cart-open")) return;
        var images = document.getElementById("cart-images");
        if (!images) return;
        if (
          rectContains(document.getElementById("cart-summary"), e.clientX, e.clientY) ||
          rectContains(document.getElementById("cart-checkout"), e.clientX, e.clientY)
        ) {
          images.scrollTop += e.deltaY;
          e.preventDefault();
        }
      },
      { passive: false }
    );
  });

  // Legacy facade — thin shim over HeaderState for any external caller that
  // still pokes window.Cart. New code should use HeaderState directly.
  window.Cart = {
    open: function () { window.HeaderState.openCart(); },
    close: function () { window.HeaderState.closeCart(); },
    refresh: refreshCartCount,
  };
})();
